import { getCurrentScope, onScopeDispose, type Ref } from "vue"
import { debounce, isEqual, omit, pick } from "lodash-es"
import { isPageUnloading } from "../../utils/pageLifecycle"
import { getViewSettingsAdapter, type ViewSettingsAdapter } from "./adapter"
import type { ViewSettingEntry } from "./entry"

// Keys with a write that failed and hasn't succeeded since. Module-scoped
// (shared across every useViewSettings() instance), not per-instance —
// save()'s no-op guard compares against the adapter, which a fresh mount
// inherits from an earlier instance's save or from the app's first load, so
// "confirmed" can't be instance-local or every remount would forget it and
// write on open again. Keyed by the full storage key string (already
// globally unique per view+identifier).
const unsyncedKeys = new Set<string>()

/**
 * Persistence policy shared by the view composables
 * (useGrid, useGallery, useKanban, useLayout): when a view's settings are read,
 * when they are written, and which of two competing copies wins. Where they are
 * kept is the registered `ViewSettingsAdapter`'s business.
 *
 * Reads are synchronous, from the adapter's session copy — populated by the app
 * at startup — so views never wait on a fetch. Writes update that copy at once
 * and reach the backing store (`adapter.write`) after a debounce, each key on
 * its own, so two views (or two tabs) saving different keys can never clobber
 * each other's write, only their own.
 *
 * `versioning` (layout keys only) carries a `baseVersion` stamp — a hash of
 * the default layout the customization derived from. When the stamp is stale
 * `versioning.onStale` reconciles the payload against the current default;
 * only a payload that actually changed is re-stamped, so a default change
 * nothing can reconcile stays visible as stale.
 *
 * `getListView` supplies a list's saved view, which competes with the user's
 * own entry by `savedAt`: whichever was saved later wins, so saving a list's
 * view reaches everyone, and a user's own change afterwards stays theirs until
 * the list's view is next saved or they reset. When the list view wins,
 * `personalKeys` (scroll, expanded groups) still come from the user's entry.
 *
 * Limits: cross-tab edits to the *same* key are still last-write-wins (two
 * tabs both resizing the same grid) — add refetch-on-focus merge only if
 * that ever bites anyone.
 */
export interface ViewSettingsVersioning {
  /** Hash of the current default this key can drift from. */
  getBaseVersion: () => string | null
  /**
   * Reconcile a stored payload against the current default, called on read
   * when the stored `baseVersion` doesn't match `getBaseVersion()`. Return the
   * updated payload — it is persisted and re-stamped — or null/undefined to
   * leave the entry and its stale stamp alone.
   */
  onStale: (payload: unknown) => unknown
}

export interface ViewSettingsOptions {
  /** Resolve the current storage key, or null when no key is available yet. */
  getKey: () => string | null | undefined
  /** Build the payload to persist from current state. */
  serialize: () => unknown
  /** `save()` is a no-op until this is true — guards against persisting before load. */
  isLoaded: Ref<boolean>
  /** Debounce window for writes, in ms. Defaults to 500. */
  debounceMs?: number
  /** Layout keys only — see `ViewSettingsVersioning`. Omit for unversioned views. */
  versioning?: ViewSettingsVersioning
  /** Called when a write to the backing store fails. */
  onWriteError?: (error: unknown) => void
  /** Called after a write succeeds — lets callers clear a "write failed" indicator. */
  onWriteSuccess?: () => void
  /** A list's saved view, which wins over an older user entry — see the header. */
  getListView?: () => ViewSettingEntry | null | undefined
  /** Payload keys that stay the user's own when the default wins. */
  personalKeys?: string[]
  /** Label used in console diagnostics, e.g. "grid". */
  label: string
  /** Use this adapter instead of the registered one. Tests only. */
  adapter?: ViewSettingsAdapter
}

export function useViewSettings(options: ViewSettingsOptions) {
  const {
    getKey,
    label,
    serialize,
    isLoaded,
    versioning,
    onWriteError,
    onWriteSuccess,
    getListView,
    personalKeys = [],
    debounceMs = 500,
  } = options
  const adapter = options.adapter ?? getViewSettingsAdapter()

  /** Write (or delete) one key. Resolves false on failure rather than rejecting. */
  function persist(key: string, entry: ViewSettingEntry | null): Promise<boolean> {
    let request: Promise<void>
    try {
      request =
        (entry === null ? adapter.erase?.(key) : adapter.write?.(key, entry)) ?? Promise.resolve()
    } catch (error) {
      request = Promise.reject(error)
    }
    return request
      .then(() => {
        unsyncedKeys.delete(key)
        onWriteSuccess?.()
        return true
      })
      .catch((error) => {
        // Mark unsynced so save()'s no-op guard doesn't mistake this failed
        // write's optimistic session copy for a confirmed one and skip the
        // retry the next time anything triggers a save for this key.
        unsyncedKeys.add(key)
        // Cut off by the page unloading, which a keepalive write outlives.
        if (isPageUnloading()) return false
        console.error(`Failed to persist ${label} settings:`, error)
        onWriteError?.(error)
        return false
      })
  }

  /**
   * Run `versioning.onStale` when the stored stamp doesn't match the current
   * default. Returns the reconciled payload, or null when there's nothing to
   * change.
   */
  function reconcile(
    payload: unknown,
    storedBase: string | undefined,
    currentBase: string | null | undefined
  ): unknown {
    if (!versioning || !currentBase || storedBase === currentBase) return null
    try {
      return versioning.onStale(payload) ?? null
    } catch (error) {
      // A throwing reconcile must never blank a user's layout.
      console.error(`Failed to reconcile stale ${label} settings:`, error)
      return null
    }
  }

  /**
   * The list view merged with the user's personal keys, when the list view
   * was saved after the user's own entry (or the user has none); else null.
   */
  function listViewOver(entry: ViewSettingEntry | undefined): Record<string, unknown> | null {
    const listView = getListView?.()
    if (!listView) return null
    if (entry && Date.parse(entry.savedAt) >= Date.parse(listView.savedAt)) return null
    const personal = entry?.payload ? pick(entry.payload as object, personalKeys) : {}
    return { ...(listView.payload as object), ...personal }
  }

  /** The payload `read()` would resolve, without persisting anything. */
  function peek<T = unknown>(): T | null {
    const key = getKey()
    if (!key) return null
    const entry = adapter.get(key)
    return (listViewOver(entry) ?? entry?.payload ?? null) as T | null
  }

  /**
   * Read the persisted payload, or null when there is none (the caller applies
   * defaults). A newer list view wins outright. A stale `baseVersion` is
   * reconciled against the current default and the result persisted back.
   */
  function read<T = unknown>(): T | null {
    const key = getKey()
    if (!key) return null
    const currentBase = versioning?.getBaseVersion?.()

    const entry = adapter.get(key)
    const fromListView = listViewOver(entry)
    if (fromListView) return fromListView as T
    if (entry && typeof entry === "object" && "payload" in entry) {
      const reconciled = reconcile(entry.payload, entry.baseVersion, currentBase)
      // `currentBase` is always set when reconcile returns a payload; testing it
      // here narrows the type instead of asserting it away.
      if (reconciled === null || !currentBase) return entry.payload as T
      const nextEntry: ViewSettingEntry = {
        ...entry,
        payload: reconciled,
        baseVersion: currentBase,
        savedAt: new Date().toISOString(),
      }
      adapter.set(key, nextEntry)
      persist(key, nextEntry)
      return reconciled as T
    }

    return null
  }

  // The backing-store write, debounced. It carries the key and entry it was
  // made for, so a write still pending when the key changes lands on its own
  // key; a save for a different key flushes it first rather than replacing it.
  let pendingKey: string | null = null
  const write = debounce((key: string, entry: ViewSettingEntry) => {
    pendingKey = null
    persist(key, entry)
  }, debounceMs)

  // A change still inside the debounce window goes out as the page goes away.
  if (getCurrentScope() && typeof window !== "undefined") {
    const flushOnHide = () => write.flush()
    window.addEventListener("pagehide", flushOnHide)
    onScopeDispose(() => window.removeEventListener("pagehide", flushOnHide))
  }

  /**
   * Guarded save. No-op until `isLoaded` and a key are available, and no-op
   * when the payload hasn't actually changed — without this, re-applying
   * just-loaded state (which every `loadSettings()` does) trips the same
   * watchers a user edit does, turning every view *open* into a write. The
   * adapter's copy updates at once, so it always holds the current settings;
   * only the backing-store write waits.
   */
  function saveNow() {
    if (!isLoaded.value) return
    const key = getKey()
    if (!key) return
    const existing = adapter.get(key)
    const payload = serialize()
    const base = existing?.baseVersion ?? versioning?.getBaseVersion?.()
    if (
      existing &&
      !unsyncedKeys.has(key) &&
      isEqual(existing.payload, payload) &&
      existing.baseVersion === base
    ) {
      return
    }
    const entry: ViewSettingEntry = { savedAt: new Date().toISOString(), payload }
    if (base) entry.baseVersion = base
    adapter.set(key, entry)
    if (pendingKey && pendingKey !== key) write.flush()
    pendingKey = key
    write(key, entry)
  }
  function cancel() {
    write.cancel()
    pendingKey = null
  }
  const save = Object.assign(saveNow, { cancel, flush: () => write.flush() })

  /** The resolved settings minus `personalKeys` — what a list's saved view takes. */
  function shared(): Record<string, unknown> | null {
    const payload = peek<Record<string, unknown>>()
    return payload ? omit(payload, personalKeys) : null
  }

  /** Remove the persisted settings and cancel pending writes. */
  function remove() {
    save.cancel()
    const key = getKey()
    if (!key) return
    adapter.remove(key)
    persist(key, null)
  }

  return { read, peek, shared, save, remove }
}
